import { Injectable } from '@angular/core';
import { chatAvatarSkillIdSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { AvatarImageService } from '../avatar-image.service';
import { HelperManagerService } from '../helper-manager.service';
import { KeyValService } from '../key-val.service';

/**
 * Setzt die Avatarbilder von Bot und Nutzer im Chat (`RAGAI.CHAT_AVATAR`).
 * @remarks
 * `skillId` kann sowohl eine echte Skill-ID (das Bild muss dazu als Bild-Skill im Bot hinterlegt sein) als auch
 * bereits eine direkte Bild-URL sein - beide Fälle werden automatisch unterschieden. Die aufgelöste URL wird in
 * `RAGAI.KEYVAL` unter dem Typ `"avatarImage"` gespeichert und von {@link AvatarImageService} beim Bot-Start
 * geladen; änderungen wirken erst nach dem nächsten `loadBotAvatarUrl()`/`loadUserAvatarUrl()`-Aufruf (wird von
 * dieser Klasse automatisch angestoßen).
 */
@Injectable({
  providedIn: 'root'
})
export class ChatAvatarManagerService {

  constructor(
    private keyvalManager: KeyValService,
    private helperManager: HelperManagerService,
    private avatarImage: AvatarImageService
  ) { }

  /**
   * Setzt das Bot-Avatarbild.
   * @param skillId Bild-URL oder Skill-ID, oder ein Objekt gemäß `chatAvatarSkillIdSchema`.
   * @returns Nichts.
   */
  public async setBotImg(skillId: string | z.input<typeof chatAvatarSkillIdSchema>): Promise<void> {
    const parsed = chatAvatarSkillIdSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      skillId = parsed.data.skillId;
    }
    let url = "";
    if ((skillId as string).startsWith('http')) {
      url = skillId as string; // If the skillId is already a URL, use it directly
    } else {
      url = await this.helperManager.getImageURlFromSkill(skillId);
    }
    await this.keyvalManager.set('botAvatar', url, 'avatarImage');
    await this.avatarImage.loadBotAvatarUrl();
  }

  /**
   * Setzt das Nutzer-Avatarbild.
   * @param skillId Bild-URL oder Skill-ID, oder ein Objekt gemäß `chatAvatarSkillIdSchema`.
   * @returns Nichts.
   */
  public async setUserImg(skillId: string | z.input<typeof chatAvatarSkillIdSchema>): Promise<void> {
    const parsed = chatAvatarSkillIdSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      skillId = parsed.data.skillId;
    }
    let url = "";
    if ((skillId as string).startsWith('http')) {
      url = skillId as string; // If the skillId is already a URL, use it directly
    } else {
      url = await this.helperManager.getImageURlFromSkill(skillId);
    }
    await this.keyvalManager.set('userAvatar', url, 'avatarImage');
    await this.avatarImage.loadUserAvatarUrl();
  }

  /** Entfernt das Bot-Avatarbild (setzt es zurück auf leer). */
  public async unsetBotImg() {
    await this.keyvalManager.set('botAvatar', '', 'avatarImage');
    await this.avatarImage.loadBotAvatarUrl();
  }

  /** Entfernt das Nutzer-Avatarbild (setzt es zurück auf leer). */
  public async unsetUserImg() {
    await this.keyvalManager.set('userAvatar', '', 'avatarImage');
    await this.avatarImage.loadUserAvatarUrl();
  }
}
